/**
 * #933 — a name the chat's Markdown export can save under on every system.
 *
 * The file was named after the conversation's title as typed: on staging,
 * 21 Oct 2026, "How many phishing campaigns do we have?.md", and Windows
 * refuses \ / : * ? " < > | and control characters in a file name, a name
 * that ends in a dot or a space, and a device name (CON, PRN, AUX, NUL,
 * COM0-9, LPT0-9), with or without an extension after it. Those characters
 * become spaces (folded into one), the ends are trimmed of dots and spaces, a
 * device name gets "chat " before it, and the name is cut at MAX_BYTES of
 * UTF-8 (most file systems allow 255 bytes in a name) at a whole character,
 * so an emoji or a flag is never split. Nothing left is "chat".
 */
const FORBIDDEN = /[\\/:*?"<>|\u0000-\u001f\u007f]/g
const DEVICE_NAME = /^(con|prn|aux|nul|com\d|lpt\d)(\.|$)/i
const MAX_BYTES = 200

type GraphemeSegmenter = { segment(text: string): Iterable<{ segment: string }> }
type SegmenterClass = new (locale?: string, options?: { granularity: 'grapheme' }) => GraphemeSegmenter

/** The characters a reader sees (an emoji or a flag is one); code points where the runtime cannot tell. */
function characters(text: string): string[] {
  const Segmenter = (Intl as unknown as { Segmenter?: SegmenterClass }).Segmenter
  if (!Segmenter) return Array.from(text)
  return Array.from(new Segmenter(undefined, { granularity: 'grapheme' }).segment(text), (part) => part.segment)
}

function utf8Length(text: string): number {
  let bytes = 0
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4
  }
  return bytes
}

function cutToBytes(text: string, max: number): string {
  let out = ''
  let bytes = 0
  for (const ch of characters(text)) {
    const size = utf8Length(ch)
    if (bytes + size > max) break
    out += ch
    bytes += size
  }
  return out
}

export function exportFileName(title: string | null | undefined, extension = 'md'): string {
  const tidy = (text: string) => text.replace(/\s+/g, ' ').replace(/^[. ]+|[. ]+$/g, '')
  const base = tidy(cutToBytes(tidy((title ?? '').replace(FORBIDDEN, ' ')), MAX_BYTES))
  if (!base) return `chat.${extension}`
  return `${DEVICE_NAME.test(base) ? `chat ${base}` : base}.${extension}`
}
